/**
 * @reflecto/core — filetransfer/crypto.ts
 *
 * Chunk-level encryption for the file-transfer path ONLY. This is a
 * SEPARATE contract from the messaging path (`../crypto.ts`, tweetnacl,
 * `[0x01 | nonce | ciphertext]` wire format) — do not mix the two.
 *
 * Byte-exact contract: see `crypto-spec.md` in this directory. The frozen
 * interop vectors in `packages/core/test-vectors/filetransfer-v1.json` are
 * the proof; both this module and the Kotlin counterpart must reproduce
 * them exactly.
 *
 * Primitives (crypto-spec.md §2):
 *   - Key derivation: HKDF-SHA-256 (RFC 5869), hand-built from HMAC-SHA-256
 *     because libsodium ships no HKDF primitive.
 *   - Chunk AEAD: XChaCha20-Poly1305-IETF, combined mode (ciphertext ‖ tag).
 *
 * Uses `libsodium-wrappers-sumo` rather than the plain `libsodium-wrappers`
 * build: the plain build's compiled WASM omits HMAC-SHA-256 entirely (no
 * `crypto_auth_hmacsha256*` symbols at all, one-shot or streaming), so it
 * cannot implement RFC 5869 HKDF. The sumo build is the same libsodium.js
 * project at the same version (0.8.4) with the full function set, and its
 * XChaCha20-Poly1305-IETF AEAD output is byte-identical to the plain
 * build's (verified against every frozen vector). `sodium.ready` must be
 * awaited before any call, per libsodium-wrappers' async WASM init.
 */

import sodium, { type StateAddress } from "libsodium-wrappers-sumo";
import { decodeUTF8 } from "tweetnacl-util";
import { AEAD_OVERHEAD, NONCE_PREFIX_LEN } from "./types";

const FT_INFO_PREFIX = decodeUTF8("reflecto-ft-v1"); // 14 ASCII bytes
const HASH_LEN = 32; // HMAC-SHA-256 output length
const HKDF_SALT_LEN = 32; // crypto-spec.md §3 — salt is 32 zero bytes
const TRANSFER_KEY_LEN = 32; // crypto-spec.md §3 — L = 32
const NONCE_LEN = NONCE_PREFIX_LEN + 8; // 24: 16-byte prefix + 8-byte BE counter

/**
 * Thrown by `decryptChunk` ONLY when the AEAD authentication tag fails to
 * verify. Wrong chunk index, wrong final flag, wrong transferId, and
 * ciphertext corruption are all indistinguishable tag failures by design
 * (crypto-spec.md §6) and all surface as this error. Input-shape problems
 * (e.g. a malformed noncePrefix) throw ordinary `Error`/`RangeError`
 * instead, before any AEAD call is made.
 */
export class ChunkAuthError extends Error {
  constructor(message = "chunk failed AEAD authentication") {
    super(message);
    this.name = "ChunkAuthError";
  }
}

function concatBytes(...parts: Uint8Array[]): Uint8Array {
  const total = parts.reduce((sum, part) => sum + part.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const part of parts) {
    out.set(part, offset);
    offset += part.length;
  }
  return out;
}

/**
 * HMAC-SHA-256. Exported for the same reason `hkdfSha256` below is: it is a seam that can be
 * asserted against published vectors (RFC 4231) directly, rather than only through a caller that
 * exercises one fixed input.
 *
 * Its second consumer is `../smsDigest.ts`, which crosses domains to reach it deliberately. A
 * five-line re-implementation of a keyed primitive is exactly the kind of duplicate that drifts
 * silently and is then wrong on one side of a two-language byte-identity contract, so the SMS
 * digest imports this one instead of growing its own.
 */
export async function hmacSha256(key: Uint8Array, message: Uint8Array): Promise<Uint8Array> {
  await sodium.ready;
  const state = sodium.crypto_auth_hmacsha256_init(key);
  sodium.crypto_auth_hmacsha256_update(state, message);
  return sodium.crypto_auth_hmacsha256_final(state);
}

/**
 * HKDF-SHA-256, RFC 5869, built from HMAC-SHA-256 (crypto-spec.md §4).
 * Exported (beyond the module's binding surface) because it's the only
 * seam that can be asserted against the RFC 5869 Appendix A test vectors
 * directly — `deriveTransferKey` only ever exercises it with the fixed
 * file-transfer salt/info shape.
 */
export async function hkdfSha256(
  ikm: Uint8Array,
  salt: Uint8Array,
  info: Uint8Array,
  length: number
): Promise<{ prk: Uint8Array; okm: Uint8Array }> {
  const prk = await hmacSha256(salt, ikm);
  const blocks = Math.ceil(length / HASH_LEN);
  if (blocks > 255) {
    throw new RangeError(
      `HKDF: requested length ${length} exceeds the 255 * ${HASH_LEN}-byte maximum`
    );
  }
  let t: Uint8Array = new Uint8Array(0);
  const blockOutputs: Uint8Array[] = [];
  for (let i = 1; i <= blocks; i++) {
    t = await hmacSha256(prk, concatBytes(t, info, new Uint8Array([i])));
    blockOutputs.push(t);
  }
  const okm = concatBytes(...blockOutputs).subarray(0, length);
  return { prk, okm };
}

/**
 * Derive the raw X25519 shared secret between two pairing keypairs
 * (crypto-spec.md §3's "ikm" input) — `crypto_scalarmult(myPriv, peerPub)`.
 *
 * This is deliberately NOT `nacl.box.before()` (the messaging path's
 * HSalsa20-hashed shared key) — the file-transfer HKDF takes the RAW
 * scalarmult output as `ikm` and does its own key derivation (§3/§4).
 * `secretKey`/`peerPublicKey` are the same 32-byte X25519 keys the pairing
 * flow already stores (`nacl.box.keyPair()` output is scalarmult-compatible
 * with libsodium's `crypto_scalarmult`, since both implement RFC 7748
 * X25519 over the same key encoding).
 */
export async function deriveSharedSecret(
  secretKey: Uint8Array,
  peerPublicKey: Uint8Array
): Promise<Uint8Array> {
  await sodium.ready;
  return sodium.crypto_scalarmult(secretKey, peerPublicKey);
}

/**
 * Streaming SHA-256 (`crypto_hash_sha256_init/update/final`), split into
 * three calls so a caller (the extension's `sendFile` loop) can hash each
 * plaintext chunk as it's read — in the SAME pass as chunk encryption —
 * without ever holding the whole file's plaintext in memory at once. Web
 * Crypto's `crypto.subtle.digest` has no incremental/streaming API, so
 * this libsodium trio is the only streaming SHA-256 available in a
 * browser context (crypto-spec.md doesn't cover this — it's a plain,
 * algorithm-defined SHA-256, not a bespoke construction).
 */
export async function sha256Init(): Promise<StateAddress> {
  await sodium.ready;
  return sodium.crypto_hash_sha256_init();
}

/** Feed one more chunk of plaintext into an in-progress streaming hash. Synchronous — `sha256Init` already awaited `sodium.ready`. */
export function sha256Update(state: StateAddress, chunk: Uint8Array): void {
  sodium.crypto_hash_sha256_update(state, chunk);
}

/** Finalize a streaming hash, returning the raw 32-byte SHA-256 digest. */
export function sha256Final(state: StateAddress): Uint8Array {
  return sodium.crypto_hash_sha256_final(state);
}

/**
 * Derive the per-transfer chunk key (crypto-spec.md §3).
 *
 * `sharedSecret` is the raw X25519 `crypto_scalarmult` output — NOT the
 * HSalsa20-hashed `crypto_box_beforenm` key used by the messaging path.
 */
export async function deriveTransferKey(
  sharedSecret: Uint8Array,
  transferId: string,
  senderPub: Uint8Array,
  recipientPub: Uint8Array
): Promise<Uint8Array> {
  const info = concatBytes(FT_INFO_PREFIX, decodeUTF8(transferId), senderPub, recipientPub);
  const salt = new Uint8Array(HKDF_SALT_LEN); // 32 zero bytes
  const { okm } = await hkdfSha256(sharedSecret, salt, info, TRANSFER_KEY_LEN);
  return okm;
}

/**
 * Build the 24-byte per-chunk nonce: `noncePrefix (16 B) ‖ chunkIndex (8 B, big-endian)`
 * (crypto-spec.md §6 nonce layout). Pure and synchronous.
 *
 * `chunkIndex` here is the TRANSFER-WIDE nonce counter, which is not always the
 * per-file chunk index: from spec version 2 (crypto-spec.md §6a) a multi-file
 * transfer feeds the file-aligned GLOBAL chunk index, so the counter never repeats
 * across files under the transfer's single key + `noncePrefix`. A single-file
 * transfer's global index equals its local one, so v1 output is unchanged.
 */
export function chunkNonce(noncePrefix: Uint8Array, chunkIndex: number): Uint8Array {
  if (noncePrefix.length !== NONCE_PREFIX_LEN) {
    throw new RangeError(
      `noncePrefix must be ${NONCE_PREFIX_LEN} bytes, got ${noncePrefix.length}`
    );
  }
  if (!Number.isInteger(chunkIndex) || chunkIndex < 0 || chunkIndex > Number.MAX_SAFE_INTEGER) {
    throw new RangeError(
      `chunkIndex must be a non-negative safe integer no greater than Number.MAX_SAFE_INTEGER, got ${chunkIndex}`
    );
  }
  const nonce = new Uint8Array(NONCE_LEN);
  nonce.set(noncePrefix, 0);
  const view = new DataView(nonce.buffer, nonce.byteOffset, nonce.byteLength);
  view.setBigUint64(NONCE_PREFIX_LEN, BigInt(chunkIndex), false); // big-endian
  return nonce;
}

/**
 * Build the chunk AAD: UTF-8 bytes of
 * `${transferId}|${fileId}|${decimal(chunkIndex)}|${isFinal ? "1" : "0"}`
 * (crypto-spec.md §6 AAD layout). Pure and synchronous.
 */
export function chunkAad(
  transferId: string,
  fileId: string,
  chunkIndex: number,
  isFinal: boolean
): Uint8Array {
  const text = `${transferId}|${fileId}|${chunkIndex}|${isFinal ? "1" : "0"}`;
  return decodeUTF8(text);
}

/**
 * The chunk index that builds the NONCE, when it differs from the AAD's
 * per-file `chunkIndex` (crypto-spec.md §6a, spec version 2).
 *
 * A transfer has ONE key and ONE random `noncePrefix`, so the 8-byte counter
 * must be unique across the WHOLE transfer, not merely within one file — with
 * a per-file counter, chunk `i` of file A and chunk `i` of file B would share a
 * (key, nonce) pair and therefore an XChaCha20 keystream. The AAD's `fileId`
 * cannot prevent that: it only feeds the Poly1305 tag, never the keystream.
 * Callers that transfer more than one file MUST therefore pass the file-aligned
 * GLOBAL chunk index (`Σ chunkCount` of every earlier manifest file + the local
 * index) here while leaving `chunkIndex` per-file.
 *
 * Defaulted to `chunkIndex` so every single-file caller — and the frozen v1
 * vectors, and Kotlin, which still declines multi-file manifests — is
 * byte-identical to spec version 1 without passing anything.
 */
type NonceChunkIndex = number;

/**
 * Encrypt one file-transfer chunk (crypto-spec.md §6). Combined-mode
 * XChaCha20-Poly1305-IETF — output is `ciphertext ‖ 16-byte tag`.
 *
 * See `NonceChunkIndex` for when `nonceChunkIndex` must be passed explicitly.
 */
export async function encryptChunk(
  key: Uint8Array,
  noncePrefix: Uint8Array,
  ids: { transferId: string; fileId: string },
  chunkIndex: number,
  isFinal: boolean,
  plaintext: Uint8Array,
  nonceChunkIndex: NonceChunkIndex = chunkIndex
): Promise<Uint8Array> {
  await sodium.ready;
  const nonce = chunkNonce(noncePrefix, nonceChunkIndex);
  const aad = chunkAad(ids.transferId, ids.fileId, chunkIndex, isFinal);
  return sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(plaintext, aad, null, nonce, key);
}

/**
 * Decrypt one file-transfer chunk. Throws `ChunkAuthError` if the AEAD
 * tag fails to verify — wrong index, wrong final flag, wrong transferId,
 * and corruption are all indistinguishable tag failures by design
 * (crypto-spec.md §6). Throws `RangeError` for input-shape problems
 * (e.g., wrong key length or too-short ciphertext) before any AEAD call.
 *
 * See `NonceChunkIndex` for when `nonceChunkIndex` must be passed explicitly —
 * it must match whatever the SENDER used, or the tag fails to verify.
 */
export async function decryptChunk(
  key: Uint8Array,
  noncePrefix: Uint8Array,
  ids: { transferId: string; fileId: string },
  chunkIndex: number,
  isFinal: boolean,
  ciphertext: Uint8Array,
  nonceChunkIndex: NonceChunkIndex = chunkIndex
): Promise<Uint8Array> {
  // Validate input shapes before any AEAD call
  if (key.length !== 32) {
    throw new RangeError(`key must be 32 bytes, got ${key.length}`);
  }
  if (ciphertext.length < AEAD_OVERHEAD) {
    throw new RangeError(
      `ciphertext must be at least ${AEAD_OVERHEAD} bytes (AEAD tag), got ${ciphertext.length}`
    );
  }

  await sodium.ready;
  const nonce = chunkNonce(noncePrefix, nonceChunkIndex);
  const aad = chunkAad(ids.transferId, ids.fileId, chunkIndex, isFinal);
  try {
    return sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(null, ciphertext, aad, nonce, key);
  } catch {
    throw new ChunkAuthError();
  }
}
